import { outputSize } from "../videoGif/encode";

import { type Decoration, loopMs } from "./effects";

/**
 * Konkord: the picture under a decoration. A still picture is one frame;
 * an animated GIF/WebP keeps all its frames (already cropped and sized), so
 * the decoration is drawn over the animation instead of a single frame.
 */
export interface Source {
  frames: ImageBitmap[];
  /** when each frame ends, in ms from the start (empty for a still) */
  ends: number[];
  /** length of the animation in ms (0 for a still) */
  duration: number;
}

/** part of the source picture, in its own pixels */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** side of an animated round picture (shown at most ~128 px, sharp on 2x) */
export const ANIMATED_SIZE = 320;
/** widest an animated banner is kept (the encoder shrinks it to fit) */
const BANNER_WIDTH = 960;

/**
 * What a cropped animation is for: a round picture (avatar, server icon),
 * a small square one (emoji, role icon) or a wide one (banner)
 */
export type AnimationUse = "round" | "square" | "wide";

/**
 * Size of the frames of a cropped animation, keeping the crop's own shape:
 * round pictures up to 320 px, square ones as the video editor makes them
 * (128 / 256 px), banners up to 960 px wide. Never larger than the crop.
 */
export function animatedCropSize(
  natural: { width: number; height: number },
  use: AnimationUse,
  maxSize: number | undefined,
) {
  if (use === "round") {
    const side = Math.round(
      Math.min(ANIMATED_SIZE, natural.width, natural.height),
    );
    return { width: side, height: side };
  }
  const shrink =
    use === "wide"
      ? Math.min(1, BANNER_WIDTH / natural.width)
      : Math.min(
          1,
          outputSize(1, maxSize).width /
            Math.max(natural.width, natural.height),
        );
  return {
    width: Math.max(16, Math.round(natural.width * shrink)),
    height: Math.max(16, Math.round(natural.height * shrink)),
  };
}

/** longest stretch of an animated picture that is kept */
const MAX_MS = 10_000;
/** frames closer than this are merged while decoding (20 fps at most) */
const MIN_FRAME_MS = 50;
/** most pixels (all frames together) kept from an animation, ~240 MB */
export const MAX_PIXELS = 60_000_000;

/** free the frames of a source (they hold GPU / memory) */
export function closeSource(source: Source | null | undefined) {
  source?.frames.forEach((frame) => frame.close());
}

export function stillSource(image: ImageBitmap): Source {
  return { frames: [image], ends: [], duration: 0 };
}

export function isAnimatedSource(source: Source) {
  return source.frames.length > 1 && source.duration > 0;
}

/** the frame showing at `ms` (wraps around) */
export function frameAt(source: Source, ms: number) {
  if (!isAnimatedSource(source)) return source.frames[0];
  const at = ms % source.duration;
  let low = 0;
  let high = source.ends.length - 1;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (source.ends[middle] > at) high = middle;
    else low = middle + 1;
  }
  return source.frames[low];
}

/**
 * Where the decoration's loop is at `ms`. Over an animation the effect
 * repeats a whole number of times within it, so both loop together.
 */
export function effectTime(source: Source, decoration: Decoration, ms: number) {
  const loop = loopMs(decoration);
  if (!isAnimatedSource(source)) return (ms / loop) % 1;
  const cycles = Math.max(1, Math.round(source.duration / loop));
  return (((ms % source.duration) / source.duration) * cycles) % 1;
}

/** the types that may hold an animation */
export function mayBeAnimated(type: string) {
  return type === "image/gif" || type === "image/webp" || type === "image/png";
}

/**
 * Every frame of an animated GIF/WebP/APNG, cropped to `rect` (the middle
 * square when missing) and scaled to the size `size` gives. At most 20 fps
 * (closer frames are merged), 10 s and `MAX_PIXELS` in all. Null when the
 * picture is not animated or the browser can't decode it frame by frame (no
 * WebCodecs): then the caller takes it as a still picture.
 */
export async function decodeAnimation(
  blob: Blob,
  rect: Rect | undefined,
  size: (natural: { width: number; height: number }) => {
    width: number;
    height: number;
  },
  signal?: AbortSignal,
): Promise<Source | null> {
  if (typeof ImageDecoder === "undefined" || !mayBeAnimated(blob.type))
    return null;
  if (!(await ImageDecoder.isTypeSupported(blob.type))) return null;
  const decoder = new ImageDecoder({ data: blob.stream(), type: blob.type });
  const frames: ImageBitmap[] = [];
  try {
    await decoder.tracks.ready;
    const track = decoder.tracks.selectedTrack;
    if (!track?.animated) return null;
    await decoder.completed;
    const count = track.frameCount;
    if (count < 2) return null;

    let canvas: HTMLCanvasElement | undefined;
    let context: CanvasRenderingContext2D | undefined;
    let output = { width: 0, height: 0 };
    let budget = Infinity;
    const ends: number[] = [];
    let time = 0;
    let keptAt = -Infinity;
    for (let i = 0; i < count && time < MAX_MS; i++) {
      signal?.throwIfAborted();
      const { image } = await decoder.decode({ frameIndex: i });
      try {
        // like browsers do: a tiny or missing delay plays as 100 ms
        const raw = (image.duration ?? 0) / 1000;
        const ms = raw > 10 ? raw : 100;
        const start = time;
        time += ms;
        // too close to the last kept frame: that one just lasts longer
        if (start - keptAt < MIN_FRAME_MS && frames.length) {
          ends[ends.length - 1] = time;
          continue;
        }
        if (frames.length >= budget) {
          time = start;
          break;
        }
        if (!canvas) {
          const side = Math.min(image.displayWidth, image.displayHeight);
          output = size({
            width: rect?.w ?? side,
            height: rect?.h ?? side,
          });
          budget = Math.max(
            2,
            Math.floor(MAX_PIXELS / (output.width * output.height)),
          );
          canvas = document.createElement("canvas");
          canvas.width = output.width;
          canvas.height = output.height;
          context = canvas.getContext("2d")!;
          context.imageSmoothingQuality = "high";
        }
        const side = Math.min(image.displayWidth, image.displayHeight);
        const area = rect ?? {
          x: (image.displayWidth - side) / 2,
          y: (image.displayHeight - side) / 2,
          w: side,
          h: side,
        };
        context!.clearRect(0, 0, output.width, output.height);
        context!.drawImage(
          image,
          area.x,
          area.y,
          area.w,
          area.h,
          0,
          0,
          output.width,
          output.height,
        );
        frames.push(await createImageBitmap(canvas));
        ends.push(time);
        keptAt = start;
      } finally {
        image.close();
      }
    }
    if (frames.length < 2) {
      frames.forEach((frame) => frame.close());
      return null;
    }
    return { frames, ends, duration: time };
  } catch (e) {
    frames.forEach((frame) => frame.close());
    throw e;
  } finally {
    decoder.close();
  }
}
